<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProducts, useProductVersions } from "@/queries/products";
import { useProjects, useProjectWorkflows } from "@/queries/projects";
import { useActivities } from "@/queries/activities";
import ActivityFeed from "@/views/shared/components/ActivityFeed.vue";
import PageHeader from "@/components/PageHeader.vue";
import OverviewTab from "./tabs/OverviewTab.vue";
import SubscribersTab from "./tabs/SubscribersTab.vue";
import VersionsTab from "./tabs/VersionsTab.vue";
import ComponentsTab from "./tabs/ComponentsTab.vue";
import AnalyticsTab from "./tabs/AnalyticsTab.vue";
import SettingsTab from "./tabs/SettingsTab.vue";
import WizardSequenceEditor from "./tabs/WizardSequenceEditor.vue";

interface Props {
  productId: string | null;
}

const props = defineProps<Props>();

const route = useRoute();
const router = useRouter();

const { products } = useProducts();
const { projects } = useProjects();

const product = computed(() => {
  if (!props.productId) return null;
  return products.value.find((p) => p.id === props.productId) || null;
});

const masterProject = computed(() => {
  if (!product.value?.masterProjectId) return null;
  return projects.value.find((p) => p.id === product.value!.masterProjectId) || null;
});

const masterProjectIdRef = computed(() => masterProject.value?.id ?? "");
const masterWorkflowsEnabled = computed(() => !!masterProjectIdRef.value);
const { projectWorkflows: masterWorkflows } = useProjectWorkflows(masterProjectIdRef, masterWorkflowsEnabled);
const hasPublishableWorkflow = computed(() =>
  masterWorkflows.value.some((w) => w.version && w.version !== "Draft"),
);

const validTabs = [
  "overview",
  "subscribers",
  "versions",
  "components",
  "analytics",
  "wizard",
  "settings",
];

const getInitialTab = () => {
  const hash = route.hash.slice(1); // Remove the '#' symbol
  return validTabs.includes(hash) ? hash : "overview";
};

const activeTab = ref(getInitialTab());

const productIdRef = computed(() => props.productId ?? "");
const { versions: productVersions } = useProductVersions(productIdRef);

// Product-scoped activity feed — surfaced in the sidebar under Quick Actions so it's visible
// on every tab (not just Overview). subjectId/Type narrow to events about THIS product.
const { activities: productActivitiesRaw, activitiesLoading: productActivitiesLoading } =
  useActivities({
    subjectType: "Product",
    subjectId: productIdRef,
    take: 10,
  });

// On the Product Detail page the viewer is always the publisher (this route is publisher-only),
// so we strip subscriber-perspective rows ("v2 is now available") and only show the producer
// side ("you published v2"). The subscriber-perspective rows are still correctly recorded —
// they surface on subscriber-project Overviews, not here. Producer rows have
// counterpartyOrgId == null; fan-out subscriber rows carry the producing org as counterparty.
const productActivities = computed(() =>
  productActivitiesRaw.value.filter((a) => !a.counterpartyOrgId),
);

const publishedProductVersion = computed(() =>
  productVersions.value.find((v) => v.status === "Published"),
);

const productStatus = computed(() => {
  const cv = publishedProductVersion.value;
  if (!cv) {
    return {
      status: "Unpublished",
      version: "—",
      workflowCount: 0,
      publishedDate: "",
      subscriberCount: 0,
    };
  }
  return {
    status: "Published",
    version: cv.versionNumber,
    workflowCount: Object.keys(cv.workflowVersionSnapshots).length,
    publishedDate: cv.publishedDate
      ? new Date(cv.publishedDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
      : "",
    subscriberCount: cv.subscriberCount ?? 0,
  };
});

const tabs = ref([
  { id: "overview", label: "Overview", component: OverviewTab },
  { id: "subscribers", label: "Subscribers", component: SubscribersTab },
  { id: "versions", label: "Versions", component: VersionsTab },
  { id: "components", label: "Components", component: ComponentsTab },
  { id: "analytics", label: "Analytics", component: AnalyticsTab },
  { id: "wizard", label: "Wizard", component: WizardSequenceEditor },
  { id: "settings", label: "Settings", component: SettingsTab },
]);

const quickActions = ref([
  {
    id: "edit-master",
    label: "Edit Master Project",
    icon: "bi bi-pencil",
    primary: true,
  },
  { id: "configure-wizard", label: "Configure Wizard", icon: "bi bi-palette" },
  {
    id: "publish-version",
    label: "Create New Version",
    icon: "bi bi-plus-circle",
  },
  {
    id: "view-analytics",
    label: "View Detailed Analytics",
    icon: "bi bi-graph-up",
  },
]);

const availableQuickActions = computed(() => {
  return quickActions.value
    .filter((action) => {
      if (action.id === "edit-master") {
        return masterProject.value !== null;
      }
      return true;
    })
    .map((action) => {
      if (action.id === "publish-version" && !hasPublishableWorkflow.value) {
        return {
          ...action,
          disabled: true,
          disabledReason:
            "Publish at least one workflow in the master project before creating a product version.",
        };
      }
      return { ...action, disabled: false, disabledReason: "" };
    });
});

function setActiveTab(tab: string) {
  if (validTabs.includes(tab)) {
    activeTab.value = tab;
    router.replace({ ...route, hash: `#${tab}` });
  }
}

// Watch for route hash changes (e.g., back/forward browser navigation)
watch(
  () => route.hash,
  (newHash) => {
    const tab = newHash.slice(1);
    if (validTabs.includes(tab)) {
      activeTab.value = tab;
    } else if (tab) {
      // Redirect to overview if trying to access invalid tab
      router.replace({ ...route, hash: "#overview" });
    }
  }
);

onMounted(async () => {
  if (!route.hash) {
    router.replace({ ...route, hash: "#overview" });
  }
});

function handleQuickAction(actionId: string) {
  switch (actionId) {
    case "edit-master":
      if (masterProject.value) {
        router.push(`/projects/${masterProject.value.id}`);
      }
      break;
    case "configure-wizard":
      setActiveTab("wizard");
      break;
    case "publish-version":
      setActiveTab("versions");
      // We'll need to emit an event or use a different approach to trigger the modal
      break;
    case "view-analytics":
      setActiveTab("analytics");
      break;
  }
}

// Two-state product status. "Published" when there's a current product version (live for
// subscribers); "Unpublished" when there isn't. Anything more nuanced belongs on the version,
// not the product — the product itself has no lifecycle field in the domain.
function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case "published":
      return "bg-primary";
    default:
      return "bg-secondary"; // "Unpublished"
  }
}
</script>

<template>
  <div v-if="product">
    <PageHeader
      :title="product.name"
      :breadcrumbs="[
        { label: 'Dashboard', href: '/' },
        { label: 'Publisher', href: '/publisher' },
        { label: 'My Products', href: '/publisher/products' },
        { label: product.name }
      ]"
    >
      <template #actions>
        <button class="bb-btn-primary-cta text-nowrap" @click="handleQuickAction('edit-master')">
          <i class="bi bi-pencil me-2"></i>Edit<span class="d-none d-md-inline"> Master Project</span>
        </button>
      </template>
    </PageHeader>

    <!-- Status Bar -->
    <div class="bg-body-tertiary p-3 rounded border mb-4">
      <!-- Mobile Status -->
      <div class="d-block d-md-none">
        <div class="d-flex align-items-center gap-2 mb-2">
          <span
            class="badge"
            :class="getStatusClass(productStatus.status)"
          >
            {{ productStatus.status }}
          </span>
          <span class="text-muted">Version {{ productStatus.version }}</span>
        </div>
        <div class="d-flex flex-wrap gap-2 text-muted small">
          <span
            ><i class="bi bi-diagram-3"></i>
            {{ productStatus.workflowCount }} workflows</span
          >
          <template v-if="productStatus.publishedDate">
            <span>•</span>
            <span
              ><i class="bi bi-calendar-check"></i>
              Published {{ productStatus.publishedDate }}</span
            >
            <span>•</span>
            <span
              ><i class="bi bi-people"></i>
              {{ productStatus.subscriberCount }}
              {{ productStatus.subscriberCount === 1 ? 'subscriber' : 'subscribers' }}</span
            >
          </template>
        </div>
      </div>

      <!-- Desktop Status -->
      <div class="d-none d-md-flex gap-4 align-items-center">
        <div class="d-flex align-items-center gap-2">
          <span
            class="badge"
            :class="getStatusClass(productStatus.status)"
          >
            {{ productStatus.status }}
          </span>
          <span class="text-muted">Version {{ productStatus.version }}</span>
          <router-link
            v-if="masterProject"
            :to="`/projects/${masterProject.id}`"
            class="badge bg-secondary text-decoration-none"
            title="Go to Master Project"
          >
            <i class="bi bi-crown-fill me-1"></i>{{ masterProject.name }}
          </router-link>
        </div>
        <div class="d-flex align-items-center gap-4 text-muted">
          <span
            ><i class="bi bi-diagram-3"></i>
            {{ productStatus.workflowCount }} workflows</span
          >
          <template v-if="productStatus.publishedDate">
            <span>•</span>
            <span
              ><i class="bi bi-calendar-check"></i>
              Published {{ productStatus.publishedDate }}</span
            >
            <span>•</span>
            <span
              ><i class="bi bi-people"></i>
              {{ productStatus.subscriberCount }}
              {{ productStatus.subscriberCount === 1 ? 'subscriber' : 'subscribers' }}</span
            >
          </template>
        </div>
      </div>
    </div>

    <ul class="nav nav-tabs mb-4" role="tablist">
      <li
        v-for="tab in tabs"
        :key="tab.id"
        class="nav-item"
        role="presentation"
      >
        <button
          class="nav-link"
          :class="{ active: activeTab === tab.id }"
          @click="setActiveTab(tab.id)"
          type="button"
          role="tab"
        >
          {{ tab.label }}
        </button>
      </li>
    </ul>

    <div class="row g-4">
      <!-- Main Tab Content -->
      <div class="col-lg-8">
        <WizardSequenceEditor
          v-if="activeTab === 'wizard' && masterProject"
          :project-id="masterProject.id"
          :project="masterProject"
        />
        <VersionsTab
          v-else-if="activeTab === 'versions'"
          :product="product"
          :master-project-id="masterProject?.id"
        />
        <ComponentsTab
          v-else-if="activeTab === 'components'"
          :product-id="product.id"
        />
        <component
          v-else
          :is="tabs.find((t) => t.id === activeTab)?.component"
          :product="product"
          :productStatus="productStatus"
          :currentVersion="publishedProductVersion"
          :masterProjectName="masterProject?.name"
        />
      </div>

      <!-- Quick Actions + Recent Activity sidebar — wider (col-lg-4) to give the activity
           feed enough room for readable rows. Visible across all tabs. -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title mb-3">Quick Actions</h2>
            <div class="d-flex flex-column gap-2">
              <template
                v-for="action in availableQuickActions"
                :key="action.id"
              >
                <span class="d-block" :title="action.disabledReason">
                  <button
                    class="w-100 text-start"
                    :class="action.primary ? 'bb-btn-primary-cta' : 'bb-btn-secondary-cta'"
                    :disabled="action.disabled"
                    @click="handleQuickAction(action.id)"
                  >
                    <i :class="action.icon"></i>
                    {{ action.label }}
                  </button>
                </span>
              </template>
            </div>
          </div>
        </div>

        <div class="card mt-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="card-title mb-0">Recent Activity</h2>
              <small v-if="productActivitiesLoading" class="text-muted">Loading…</small>
            </div>
            <ActivityFeed
              :activities="productActivities"
              :loading="productActivitiesLoading"
              empty-text="No product activity yet. Version publishes and subscriptions will appear here."
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading/Error State -->
  <div
    v-else
    class="d-flex justify-content-center align-items-center"
    style="height: 50vh"
  >
    <div class="text-center">
      <i class="bi bi-exclamation-triangle display-1 text-muted"></i>
      <h3 class="mt-3 text-muted">Product not found</h3>
      <a href="/publisher" class="bb-btn-primary-cta">
        <i class="bi bi-arrow-left"></i> Back to Dashboard
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* ProductDetailView mobile layout stacks buttons below title for better viewport fit */
</style>
